/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import React, { StrictMode } from 'react';
import { IntlProvider } from 'react-intl';
import { Provider } from 'react-redux';

import MainRoutesWrapper from '@ibm-watson/shared/modules/ia/components/app/MainRoutesWrapper';
import { CustomHistoryRouter } from '@ibm-watson/shared/modules/ia/components/custom-history-router/CustomHistoryRouter';
import PortalConductor from '@ibm-watson/shared/modules/ia/components/portal-conductor/PortalConductor';
import Segment from '@ibm-watson/shared/modules/ia/components/segment/Segment';
import UserActivityMonitor from '@ibm-watson/shared/modules/ia/components/user-activity-monitor/UserActivityMonitor';
import { activityMonitor } from '@ibm-watson/shared/modules/ia/util/config';
import history from '@ibm-watson/shared/modules/ia/util/history';
import { Language } from '@ibm-watson/shared/ts/skill';
import MainRoutes from './containers/app/MainRoutes';
import { getStore } from './store';

const store = getStore();

/**
 * The is the top level of our application.
 *
 * This component takes care of the general setup required for a React
 * application (global context providers for internationalization, Redux
 * and history) and renders global components specific to our application
 * (for issuing Segment events, monitoring user activity to know when to
 * refresh the user token in CPD, and being able to render the different
 * modals of the application).
 *
 * It also renders the main routes of the application, wrapped in a
 * component that initiates the authentication process if the user is
 * not yet authenticated.
 *
 * This component is common between experiences (i.e. Classic and New
 * IA) and also between SaaS and WXO.
 */
export const AppShell: React.FC = () => (
  <StrictMode>
    <IntlProvider locale={Language.English} defaultLocale={Language.English}>
      <Provider store={store}>
        <CustomHistoryRouter history={history}>
          <MainRoutesWrapper>
            <MainRoutes />
          </MainRoutesWrapper>
          <Segment />
          <UserActivityMonitor
            enabled={activityMonitor.enabled}
            interval={activityMonitor.interval}
          />
          <PortalConductor />
        </CustomHistoryRouter>
      </Provider>
    </IntlProvider>
  </StrictMode>
);
